import React from 'react';
import { observer } from 'mobx-react-lite';
import { getDeceptioScoreLevel, isDeceptiveLevel, PlayerTranscriptsState } from './playerTranscriptsState';
import { PlayerMomentTime } from '../playerShared/playerMomentTime';
import { useAnchor } from '../../services/overlays';
import { PlayerEditMomentButton } from '../playerShared/playerEditMomentButton';
import { MomentModel } from '../../entities/moment';
import classNames from 'classnames';
import { SyncStatus } from '../../entities';
import { MomentSearchAtom } from './playerTranscriptsState';
import {
  buildTranscriptConfidenceSegments,
  getMomentConfidenceLevel,
  getSearchMatchForSegment,
  TranscriptConfidenceSegment,
} from './transcriptConfidence';
import { isSubjectSpeaker } from '../playerIndex/playerIndexSpeakerList';

type Props = {
  pageModel: PlayerTranscriptsState,
  moment: MomentModel
}

export const PlayerTranscriptBlock = observer((props: Props) => {

  const { pageModel, moment } = props;
  const { player } = pageModel;
  const { momentView } = player;

  const anchorId = 'videoTranscriptMoment#' + moment.id;
  const [anchorRef] = useAnchor({
    target: moment,
    id: anchorId
  });

  const selectedSearchMatch = pageModel.selectedSearchMatch;
  const selectedIndexPair = (moment.id === selectedSearchMatch?.momentId && selectedSearchMatch?.indexPair) || null;
  const searchMatches = pageModel.getMomentMatches(moment.id);
  const isInlineEditing = pageModel.isInlineEditingTranscript(moment.id);
  const isInlineSaving = pageModel.isInlineSavingTranscript(moment.id);
  const transcriptDescription = pageModel.getMomentDescription(moment);
  const wordConfidence = pageModel.getMomentWordConfidence(moment);
  const momentConfidenceLevel = getMomentConfidenceLevel(moment.computedMinConfidence);
  const deceptioScore = pageModel.isTranscriptDeceptioActive ?
    pageModel.getDeceptioScoreForTranscript(moment) :
    null;
  const deceptioLevel = getDeceptioScoreLevel(deceptioScore);
  const hasDeceptioConnectorAfter = pageModel.isTranscriptDeceptioActive && !!deceptioLevel &&
    pageModel.hasDeceptioConnectorAfterTranscript(moment);
  const hasDeceptioConnectorBefore = pageModel.isTranscriptDeceptioActive && !!deceptioLevel &&
    pageModel.hasDeceptioConnectorBeforeTranscript(moment);
  const isDeceptioContinuation = hasDeceptioConnectorBefore;
  const shouldShowDeceptioBadge = pageModel.isTranscriptDeceptioActive && deceptioLevel &&
    typeof deceptioScore === 'number' && !isDeceptioContinuation;
  const hasWordSegments = wordConfidence.length > 0;
  let textContent = transcriptDescription ?
    generateTranscriptConfidenceContent({
      segments: hasWordSegments ?
        buildTranscriptConfidenceSegments(transcriptDescription, wordConfidence).map(segment =>
          hideTranscriptConfidenceSegment(segment, pageModel.isTranscriptConfidenceVisible)) :
        [{ text: transcriptDescription, start: 0, end: transcriptDescription.length }],
      searchMatches,
      selectedIndexPair,
    }) :
    null;

  textContent = (momentView.activeTranscript === moment) ?
    <p><mark className="mark-wrapper"><span className="wrapper">{textContent}</span></mark></p> :
    <p>{textContent}</p>

  const job = player?.job;

  const isEditable: boolean = (
    job?.speakersSyncStatus === SyncStatus.Fetched &&
    job?.hasPermission('EditUserMoment') &&
    moment.userHasEditPermission) ?? false;

  const isSubjectRow = isSubjectSpeaker(moment.actualSpeakerId ?? '', job?.deceptioSpeakerId);

  const className = classNames('user-player-transcript-box', {
    'active': momentView.activeTranscript === moment,
    'editable': isEditable,
    'has-confidence': pageModel.isTranscriptConfidenceVisible && !!momentConfidenceLevel,
    'has-deceptio-score': pageModel.isTranscriptDeceptioActive && !!deceptioLevel,
    'has-deceptio-connector-before': hasDeceptioConnectorBefore,
    'has-deceptio-connector-after': hasDeceptioConnectorAfter,
    'is-deceptio-continuation': isDeceptioContinuation,
    'inline-editing': isInlineEditing,
    'inline-saving': isInlineSaving,
  });

  const enterInlineEdit = (evt: React.MouseEvent) => {
    if (!isEditable || isInlineEditing)
      return;

    evt.preventDefault();
    evt.stopPropagation();
    pageModel.enterInlineTranscriptEdit(moment);
  };

  return (
    <div ref={anchorRef as React.Ref<HTMLDivElement>}
      data-tutorial-id="user-player-transcript-box"
      className={className}
      data-confidence-level={pageModel.isTranscriptConfidenceVisible ? momentConfidenceLevel ?? undefined : undefined}
      data-deceptio-level={pageModel.isTranscriptDeceptioActive ? deceptioLevel ?? undefined : undefined}
      onClick={evt => pageModel.handleTranscriptClick(evt, moment, isEditable)}
      onDoubleClick={enterInlineEdit}>
      <div className="transcript-gutter">
        <div className="gutter-content">
          <PlayerMomentTime
            model={moment}
            showEndTime={false} />

          {shouldShowDeceptioBadge &&
            <button
              type="button"
              className={`transcript-deceptio-score deceptio-${deceptioLevel}`}
              title="View Deceptio analysis"
              onClick={evt => {
                evt.preventDefault();
                evt.stopPropagation();
                pageModel.openDeceptioAnalysisForTranscript(moment);
              }}>
              {deceptioScore}%
            </button>}

          {shouldShowDeceptioBadge && isDeceptiveLevel(deceptioLevel) &&
            <button
              type="button"
              className="transcript-deceptio-next"
              aria-label="Jump to next deceptive statement"
              title="Jump to next deceptive statement"
              onDoubleClick={evt => evt.stopPropagation()}
              onClick={evt => {
                evt.preventDefault();
                evt.stopPropagation();
                pageModel.jumpToNextDeceptive(moment);
              }}>
              Next
            </button>}

          {isEditable &&
            <PlayerEditMomentButton
              player={player}
              moment={moment}
              permissionMode="always" />}
        </div>
      </div>

      <div className="transcript-text">
        {pageModel.areTranscriptSpeakersVisible && moment.speaker?.name && (
          <div className="transcript-speaker">
            {moment.speaker.name}
            {isSubjectRow &&
              <span className="transcript-subject-tag"> (Subject)</span>}
          </div>
        )}
        {isInlineEditing ?
          <div className="transcript-inline-editor"
            onBlur={evt => pageModel.handleInlineTranscriptEditorBlur(evt, moment)}
            onClick={evt => evt.stopPropagation()}>
            <div className="transcript-inline-confidence-preview">
              {textContent}
            </div>
            <textarea
              ref={pageModel.setInlineEditorElement}
              value={pageModel.inlineEditValue}
              disabled={isInlineSaving}
              rows={3}
              onChange={evt => pageModel.setInlineTranscriptEditValue(evt.target.value)}
              onKeyDown={evt => pageModel.handleInlineTranscriptEditKeyDown(evt, moment)} />
            <div className="transcript-inline-editor-actions">
              <button
                type="button"
                disabled={isInlineSaving}
                onMouseDown={evt => evt.preventDefault()}
                onClick={() => pageModel.cancelInlineTranscriptEdit()}>
                Cancel
              </button>
              <button
                type="button"
                disabled={isInlineSaving}
                onMouseDown={evt => evt.preventDefault()}
                onClick={() => void pageModel.saveInlineTranscriptEdit(moment)}>
                {isInlineSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div> :
          textContent}
      </div>
    </div>
  )
});

type GenerateTranscriptConfidenceContentProps = {
  segments: TranscriptConfidenceSegment[];
  searchMatches: MomentSearchAtom[];
  selectedIndexPair?: [number, number] | null;
};

const hideTranscriptConfidenceSegment = (
  segment: TranscriptConfidenceSegment,
  isConfidenceVisible: boolean,
): TranscriptConfidenceSegment => {
  if (isConfidenceVisible)
    return segment;

  return {
    text: segment.text,
    start: segment.start,
    end: segment.end,
  };
};

const generateTranscriptConfidenceContent = ({
  segments,
  searchMatches,
  selectedIndexPair,
}: GenerateTranscriptConfidenceContentProps) => {
  return (
    <p>
      {segments.map((segment, index) => {
        const searchMatch = getSearchMatchForSegment(segment, searchMatches);
        const isSelectedSearchMatch = !!(
          searchMatch &&
          selectedIndexPair &&
          searchMatch.indexPair[0] === selectedIndexPair[0] &&
          searchMatch.indexPair[1] === selectedIndexPair[1]
        );

        if (!segment.level && !searchMatch)
          return <React.Fragment key={index}>{segment.text}</React.Fragment>;

        const className = classNames({
          'confidence-word': !!segment.level,
          [`confidence-${segment.level}`]: !!segment.level,
          highlighted: !!searchMatch,
          selected: isSelectedSearchMatch,
        });

        return (
          <span
            key={index}
            className={className}
            data-confidence-level={segment.level}
            data-confidence={segment.confidence}
            data-match={searchMatch ? true : undefined}
            data-match-id={searchMatch?.globalIndex}>
            {segment.text}
          </span>
        );
      })}
    </p>
  );
}
